import React from 'react';
import { EpisodeListItem } from '../../hooks/useEpisodes';
import { formatDateFr } from '../../services/dateFormat';

interface EpisodeCardProps {
  episode: EpisodeListItem;
  onEdit: (id: string) => void;
  onPresent: (id: string) => void;
  onDelete: (id: string) => void;
  onEditAttributes: (id: string) => void;
}

export const EpisodeCard: React.FC<EpisodeCardProps> = ({ episode, onEdit, onPresent, onDelete, onEditAttributes }) => {
  const formattedDate = episode.date ? formatDateFr(episode.date) : '';

  const handleCardClick = () => {
    onEdit(episode.id);
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      data-testid={`episode-card-${episode.id}`}
      onClick={handleCardClick}
      className="group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden border border-gray-700 hover:border-orange-500 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/10 hover:-translate-y-1"
    >
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-600" />
      
      {/* Top right actions - appear on hover */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
        <button
          data-testid="edit-attributes-button"
          onClick={(e) => handleButtonClick(e, () => onEditAttributes(episode.id))}
          className="p-2 text-gray-500 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
          title="Modifier les attributs"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          data-testid="delete-button"
          onClick={(e) => handleButtonClick(e, () => onDelete(episode.id))}
          className="p-2 text-gray-500 hover:text-white hover:bg-red-600 rounded-lg transition-colors"
          title="Supprimer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-5">
        {/* Episode number badge and show title */}
        <div className="flex items-center gap-3 mb-4">
          {episode.number != null && (
            <span className="text-4xl font-bold text-orange-500">
              #{episode.number}
            </span>
          )}
          {episode.showTitle && (
            <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
              {episode.showTitle}
            </span>
          )}
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2" data-testid="episode-title">
          {episode.title}
        </h3>
        
        {/* Date */}
        {formattedDate && (
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-4" data-testid="episode-date">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formattedDate}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-700">
          <button
            data-testid="present-button"
            onClick={(e) => handleButtonClick(e, () => onPresent(episode.id))}
            className="flex-1 px-3 py-2 text-sm bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Présenter
          </button>
          <button
            data-testid="studio-button"
            onClick={(e) => handleButtonClick(e, () => onEdit(episode.id))}
            className="flex-1 px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            Studio
          </button>
        </div>
      </div>
    </div>
  );
};
